import { useState, useMemo, KeyboardEvent, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { MapPinIcon } from "lucide-react";

import { SearchAutocomplete } from "@/components/inputs/search-autocomplete/search-autocomplete";
import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { Input } from "@/components/ui/input";

import { useFilters } from "@/hooks/use-filters";
import { SearchSpace, useSearchSpaces } from "@/hooks/useSearchSpaces";
import { SPACE_TYPE } from "@/constants/space-type";
import { debounce } from "lodash";

interface LeftComponentsProps {
  setSearchSelectedValue: (value: string) => void;
  handleSearchSubmitAuto: (locationSearchValue?: string) => void;
}

type SearchType = "location" | "space" | "privateOfficeIds" | "neighborhoods";

export function LeftComponents({
  setSearchSelectedValue,
  handleSearchSubmitAuto,
}: LeftComponentsProps) {
  const { setValue } = useFormContext();

  const {
    handSelectedCity,
    filterCities,
    handSelectedNeighborhood,
    filterNeighborhoods,
    handSelectedSpace,
    filterSpaces,
    setNeighborhoods,
  } = useFilters();

  const {
    isLoading: isLoadingSpaces,
    spaces,
    handleSearchSpaces,
  } = useSearchSpaces();

  const [searchType, setSearchType] = useState<SearchType>("privateOfficeIds");
  const [selectedCity, setSelectedCity] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedSpace, setSelectedSpace] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [roomId, setRoomId] = useState("");

  const searchOptions = useMemo(
    () => [
      { id: 1, name: "ID da sala", type: "privateOfficeIds" },
      { id: 2, name: "Espaço", type: "space" },
      { id: 3, name: "Localização", type: "location" },
      { id: 4, name: "Múltiplos bairros", type: "neighborhoods" },
    ],
    [],
  );

  const debouncedAutoSearch = useMemo(
    () => debounce(() => handleSearchSubmitAuto(), 200),
    [handleSearchSubmitAuto],
  );

  useEffect(() => {
    if (selectedSpace || selectedNeighborhoods.length) {
      debouncedAutoSearch();
    }
    return () => debouncedAutoSearch.cancel();
  }, [selectedSpace, selectedNeighborhoods, debouncedAutoSearch]);

  const resetAllFields = () => {
    setValue("spaceId", undefined);
    setValue("privateOfficeIds", undefined);
    setValue("cityName", undefined);
    setValue("neighborhood", undefined);
    setSelectedCity(null);
    setSelectedNeighborhoods([]);
    setSelectedSpace(null);
    setRoomId("");
    setSearchSelectedValue("");
  };

  const clearIrrelevantFields = (type: SearchType) => {
    if (type !== "space") setValue("spaceId", undefined);
    if (type !== "privateOfficeIds") setValue("privateOfficeIds", undefined);
    if (type !== "neighborhoods") {
      setValue("cityName", undefined);
      setValue("neighborhood", undefined);
    }
  };

  const handleCitySelect = (item: { id: number; name: string }) => {
    clearIrrelevantFields("neighborhoods");
    setSelectedCity(item);
    setNeighborhoods(undefined);
    setSelectedNeighborhoods([]);
    setValue("cityName", item.name);
  };

  const handleNeighborhoodSelect = (item: { id: number; name: string }) => {
    clearIrrelevantFields("neighborhoods");
    setSelectedNeighborhoods((prev) => {
      const exists = prev.some((n) => n.id === item.id);
      const updated = exists
        ? prev.filter((n) => n.id !== item.id)
        : [...prev, item];

      setValue(
        "neighborhood",
        updated.map((n) => n.name),
      );
      return updated;
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmitAuto();
    }
  };

  const handleSearchAutoCompleteChange = (
    _value: string,
    data: SearchSpace | undefined,
  ) => {
    const selectedValue = data ? `${data.name} - ${data.address}` : "";
    setSearchSelectedValue(selectedValue);

    if (selectedValue) {
      handleSearchSubmitAuto(selectedValue);
    }
  };

  const searchTypeComponents: Record<SearchType, JSX.Element> = {
    location: (
      <SearchAutocomplete
        isLoading={isLoadingSpaces}
        options={spaces.map((space) => ({
          value: space.placeId,
          label: space.name,
          description: space.address,
          icon: <MapPinIcon className="w-4" />,
          data: space,
        }))}
        placeholder="Busque por endereço, espaço, referência ou sala privativa"
        onInputChange={(value) => {
          setSearchSelectedValue(value);
          handleSearchSpaces(value);
        }}
        onValueChange={handleSearchAutoCompleteChange}
      />
    ),

    space: (
      <div className="min-w-[320px]">
        <SearchInputCommand
          name="spaces"
          selectOption={(_, item) => {
            clearIrrelevantFields("space");
            setSelectedSpace(item);
            setValue("spaceId", item.id);
          }}
          searchValue={selectedSpace?.name || ""}
          optionsList={filterSpaces || []}
          updateList={(term: string) =>
            handSelectedSpace(term, [
              SPACE_TYPE.COWORKING,
              SPACE_TYPE.FLOOR_BTS,
            ])
          }
          label="Busque por nome do espaço"
          inputWidth="w-full"
        />
      </div>
    ),

    privateOfficeIds: (
      <div className="min-w-[320px]">
        <Input
          placeholder="Digite o ID da sala"
          value={roomId}
          onChange={(e) => {
            const value = e.target.value;
            clearIrrelevantFields("privateOfficeIds");
            setRoomId(value);
            const ids = value
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id.length > 0);
            setValue("privateOfficeIds", ids.length > 0 ? ids : null);
          }}
          className="w-full"
          onKeyDown={handleKeyDown}
        />
      </div>
    ),

    neighborhoods: (
      <div className="flex gap-2 items-center">
        <div className="min-w-[320px]">
          <SearchInputCommand
            name="cities"
            selectOption={(_, item) => handleCitySelect(item)}
            searchValue={selectedCity?.name || ""}
            optionsList={filterCities || []}
            updateList={handSelectedCity}
            label="Cidade"
            inputWidth="w-full"
          />
        </div>
        <div className="min-w-[320px] max-w-[420px] 2xl:max-w-[720px]">
          <SearchInputCommand
            name="neighborhood"
            chevron
            selectOption={(_, item) => handleNeighborhoodSelect(item)}
            selectedList={selectedNeighborhoods}
            searchValue=""
            optionsList={filterNeighborhoods || []}
            updateList={(term: string) =>
              handSelectedNeighborhood(term, selectedCity?.name || "")
            }
            label="Bairros"
            inputWidth="w-full"
            disabled={!selectedCity}
          />
        </div>
      </div>
    ),
  };

  return (
    <div className="w-full flex gap-2 items-center">
      <div className="min-w-[240px]">
        <SearchInputCommand
          name="searchType"
          chevron
          searchDisable
          searchValue={
            searchOptions.find((opt) => opt.type === searchType)?.name || ""
          }
          selectOption={(_, item) => {
            const selected = searchOptions.find((opt) => opt.id === item.id);
            setValue("searchType", selected?.type || "privateOfficeIds");
            if (!selected) return;
            resetAllFields();
            setTimeout(() => setSearchType(selected.type as SearchType), 0);
          }}
          optionsList={searchOptions}
          label="ID da sala"
          inputWidth="w-[240px]"
        />
      </div>
      {searchTypeComponents[searchType]}
    </div>
  );
}
